import React, { useEffect } from 'react'
import { CompanyId, CustomType, GoalWithProgress, Lang, ProgressType, Role } from '../../services/api.types'
import { List, ListItem, ListItemIcon, ListItemText, Typography } from '@material-ui/core'
import { msgid, ngettext, t } from 'ttag'
import { convertUTCToLocalDate, getPeriodString } from '../../common/helpers/date'
import { isCollectiveType, isPersistenceType } from '../../common/helpers/challenges'
import { formatThousands, getDailyGoalString, getProgressTypeString } from '../../common/helpers/translations'
import makeStyles from '@material-ui/core/styles/makeStyles'

import { ReactComponent as TitleIcon } from '../../assets/v3/icons/challenge-info/title.svg'
import { ReactComponent as PeriodIcon } from '../../assets/v3/icons/challenge-info/period.svg'
import { ReactComponent as PrizeIcon } from '../../assets/v3/icons/challenge-info/prize.svg'
import { ReactComponent as DailyGoalIcon } from '../../assets/v3/icons/challenge-info/daily-goal.svg'
import { ReactComponent as TotalGoalIcon } from '../../assets/v3/icons/challenge-info/total-goal.svg'
import { ReactComponent as DescriptionIcon } from '../../assets/v3/icons/challenge-info/description.svg'
import { formatDescription } from '../../common/helpers/activitiesHelpers'
import NotificationsWrapper from '../../components/Notification/NotificationsWrapper'
import YumGrid from '../../components/YumGrid/YumGrid'
import { IJoinGoalPayload } from '../Activity/store/ActivitySagas'
import YumChallengeActions from '../../components/YumChallengeActions/YumChallengeActionsWrapper'
import {
  CustomTypeCategory,
  getActivityType,
  getCustomCategory,
  getCustomTypeTextById,
  getCustomUnitText,
} from '../../services/CustomTypes'

import { calculateDuration } from '../ChallengeAdmin/ChallengeAdmin'
import { PointsLogic } from '../ChallengeAdmin/components/ChallengePoints/ChallengePoints'
import { unitsCountPointsShortFunc } from '../../common/helpers/units'
import { YumActivityIcon } from '../../components/YumActivityIcon/YumActivityIcon'
import { currentLang } from '../../i18nInit'

export const dailyMaxLabel = currentLang === Lang.EN ? `Daily max` : t`Daily maximum`

export interface IChallengeInfoProps {
  goal?: GoalWithProgress
  getCurrentCompanyGoal(): void
  joinGoal(payload: IJoinGoalPayload): void
  hasRoleInCompany(companyId: CompanyId, role: Role): boolean
}

export const useStyles = makeStyles((theme) => ({
  wrapper: {
    paddingBottom: '16px',
  },
  grid: { marginBottom: '20px', alignItems: 'center' },
  logo: {
    display: 'inline-block',
    height: '40px',
    width: 'auto',
    maxWidth: '100%',
    boxSizing: 'content-box',
  },
  listItem: { flexWrap: 'nowrap', alignItems: 'start', padding: '10px', marginBottom: '8px' },
  listItemDense: { padding: 0, marginBottom: '10px', background: 'transparent', '&:last-child': { marginBottom: 0 } },
  listItemText: { marginTop: 0, marginBottom: 0 },
  listItemTextPrimary: { fontWeight: 700, fontSize: '11px', lineHeight: '13px', opacity: 0.7, marginBottom: '2px' },
  listItemTextSecondary: { fontWeight: 500, fontSize: '13px', lineHeight: '16px', color: 'inherit' },
}))

export const getListItems =
  (classes: any, isPoints?: boolean) =>
  (
    {
      label,
      content,
      Icon,
    }: {
      label: string
      content: any
      Icon: any
    },
    index: number,
  ) => {
    const listItemClass = [classes.listItem]
    if (isPoints) listItemClass.push(classes.listItemDense)
    return (
      <ListItem className={listItemClass.join(' ')} key={index}>
        <ListItemIcon>{Icon}</ListItemIcon>
        <ListItemText
          className={classes.listItemText}
          primary={label}
          secondary={content}
          primaryTypographyProps={{
            className: classes.listItemTextPrimary,
          }}
          secondaryTypographyProps={{
            className: classes.listItemTextSecondary,
            component: 'div',
          }}
        />
      </ListItem>
    )
  }

export const PointsFormula = ({
  pointsLogic,
  isReview,
  style,
}: {
  pointsLogic: PointsLogic[] | null
  isReview?: boolean
  style?: any
}) => {
  if (!pointsLogic || !pointsLogic.length) return null
  const classes = useStyles()

  if (!style) style = {}
  style.marginTop = isReview ? 0 : 10
  style.marginBottom = 0

  const getPointsData = (pointsLogic: PointsLogic[]) => {
    return pointsLogic.map((activity) => {
      const isCustom = [CustomTypeCategory.CUSTOM_SIMPLE, CustomTypeCategory.CUSTOM_WITH_AMOUNT].includes(
        activity.category,
      )
      const isCustomSimple = activity.category === CustomTypeCategory.CUSTOM_SIMPLE
      const value = formatThousands(activity.value)
      const unit = isCustom
        ? isCustomSimple
          ? t`Done`.toLowerCase()
          : activity.customUnitName
        : getCustomUnitText(activity.unit)
      const pointsAmount = formatThousands(activity.points)
      const pointsUnit = unitsCountPointsShortFunc(activity.points)
      const maxPointsUnit = unitsCountPointsShortFunc(activity.dailyGoal)
      // const description = activity.description ? ` (${activity.description})` : ''
      return {
        label: (isCustom ? activity.customActivityName : getCustomTypeTextById(activity.category)) as string,
        content: (
          <>
            <div>{`${value} ${unit} = ${pointsAmount} ${pointsUnit}`}</div>
            {activity.dailyGoal && activity.unit !== CustomType.CUSTOM && (
              <div style={{ opacity: 0.7, fontSize: '11px' }}>{`${dailyMaxLabel}: ${formatThousands(
                activity.dailyGoal,
              )} ${maxPointsUnit}`}</div>
            )}
          </>
        ),
        Icon: <YumActivityIcon category={activity.category} color={'darkBlue'} isInactive={true} extraPadding={true} />,
      }
    })
  }

  return (
    <List style={{ marginTop: isReview ? 0 : 10 }}>{getPointsData(pointsLogic).map(getListItems(classes, true))}</List>
  )
}

export default function ChallengeInfo(props: IChallengeInfoProps) {
  const { goal, getCurrentCompanyGoal, hasRoleInCompany, joinGoal } = props

  useEffect(() => {
    getCurrentCompanyGoal()
  }, [])

  if (!goal) return <></>

  const classes = useStyles()

  const getOrganizer = () => {
    const { logoUrl, companyLogo, companyName } = goal
    const logo = logoUrl || companyLogo
    if (!logo)
      return (
        <div>
          <Typography variant="caption">{companyName}</Typography>
        </div>
      )
    const isSvg = logo.endsWith('.svg')
    return (
      <div>
        <img
          className={classes.logo}
          style={{
            height: !isSvg ? '30px' : '40px',
            padding: !isSvg ? '10px' : '0',
            background: !isSvg ? 'white' : 'transparent',
            borderRadius: !isSvg ? '8px' : '0',
          }}
          src={logo}
          alt={companyName}
        />
      </div>
    )
  }

  const getData = (goal: GoalWithProgress) => {
    const {
      title,
      description,
      startDate: startDateStamp,
      endDate: endDateStamp,
      progressType,
      voucher,
      persistenceGoal,
      customType,
      customName,
    } = goal
    const data = []

    const isTotalAmountChallenge = false
    const isConsistencyChallenge = isPersistenceType(progressType)
    const isSimpleConsistencyChallenge = false
    const isCollectiveChallenge = isCollectiveType(progressType)

    const startDate = convertUTCToLocalDate(new Date(startDateStamp))
    const endDate = convertUTCToLocalDate(new Date(endDateStamp))
    const days = calculateDuration(startDate, endDate)
    const daysText = days ? ngettext(msgid`day`, `days`, days) : ''

    if (title) {
      data.push({
        label: t`Challenge name`,
        content: title,
        Icon: <TitleIcon />,
      })
    }
    data.push({
      label: t`Period`,
      content: getPeriodString(startDateStamp, endDateStamp) + ` (${days} ${daysText})`,
      Icon: <PeriodIcon />,
    })
    if (progressType !== ProgressType.PERSISTENCE_CUSTOM_BINARY) {
      const pointsLogic = (goal.customType === CustomType.POINTS ? JSON.parse(goal.pointsLogic) : []) as PointsLogic[]
      const category = getCustomCategory(progressType, customType)
      data.push({
        label: t`Activity type`,
        //content: getActivityType(goal) + (customType === CustomType.POINTS ? <PointsFormula goal={goal} /> : ''),
        content: (
          <>
            {getActivityType(goal)}
            <PointsFormula pointsLogic={pointsLogic} />
          </>
        ),
        Icon: <YumActivityIcon category={category} bg={'gray'} />,
      })
    }
    if (!isConsistencyChallenge) {
      data.push({
        label: isCollectiveChallenge ? t`Collective goal` : t`Goal`,
        content: getProgressTypeString(goal, true),
        Icon: <TotalGoalIcon />,
      })
    }
    if (isConsistencyChallenge) {
      data.push({
        label: t`Daily goal`,
        content: getDailyGoalString(goal),
        Icon: <DailyGoalIcon />,
      })
    }
    if (isConsistencyChallenge) {
      data.push({
        label: t`Total goal`,
        content: t`Achieve daily target for ${persistenceGoal} days`,
        Icon: <TotalGoalIcon />,
      })
    }
    if (voucher) {
      data.push({
        label: t`Prize`,
        content: voucher,
        Icon: <PrizeIcon />,
      })
    }
    if (description) {
      data.push({
        label: t`Description`,
        content: <span style={{ whiteSpace: 'pre-line' }}>{formatDescription(description)}</span>,
        Icon: <DescriptionIcon />,
      })
    }

    return data
  }

  return (
    <div className={classes.wrapper}>
      <NotificationsWrapper />

      <YumGrid className={classes.grid}>
        {getOrganizer()}
        <div>
          <YumChallengeActions goal={goal} />
        </div>
      </YumGrid>

      <List>{getData(goal).map(getListItems(classes))}</List>
    </div>
  )
}
